// blueprintCacheSync — what a SUCCESSFUL Save to Cloud must do to the
// per-graph localStorage caches.
//
// WHY THIS EXISTS (ENG-1120). The post-save cleanup used to be a blanket
// prefix sweep:
//
//   for (const k of Object.keys(localStorage))
//     if (k.startsWith('aos-blueprint')) localStorage.removeItem(k)
//
// Its INTENT was right — after a push the server is authoritative, so no local
// draft may silently shadow it on the next visit (the stale-localStorage bug
// Florent hit). Its MECHANISM was not. That prefix covers three different
// kinds of key, and deleting two of them is destructive:
//
//   • the per-graph caches themselves (aos-blueprint-v6 / -face-v7 / -events-v7,
//     per user and per character). Exactly ONE editor surface is mounted at a
//     time, and BlueprintGraph seeds its canvas from its cache AT MOUNT. So the
//     graph you were looking at survived in React state and every other graph
//     came back from a key that no longer existed — a lone Final Pose node,
//     which reads as "my face graph is gone". (ENG-1120: open Face, open
//     Events, Save to cloud, back to Face = empty. The data was never lost;
//     Load from Cloud restored it, which is what proves the SAVE was fine and
//     the cleanup was the bug.)
//   • aos-blueprint-active-{uid}-{charId}-{kind} — the tag recording WHICH
//     library row (ENG-1030) each graph was loaded from. Without it the next
//     save routes to the character's default instead of the open row, which is
//     the precise failure activeBlueprintAsset.js exists to prevent.
//   • every OTHER character's caches, by the same mechanism, for no stated
//     reason.
//
// The fix follows from what a successful save actually establishes: the caches
// now EQUAL the server. So REWRITE them with what was just pushed rather than
// deleting them. The shadow the sweep was defending against cannot exist — a
// cache that matches the server shadows nothing — and the editor keeps the
// invariant it remounts against.
//
// Pure over an injected storage object, so it runs under plain node:test;
// BlueprintEditor.jsx (the only caller) cannot be imported there.

/**
 * The writes a successful save owes the caches: one per graph present in the
 * payload. A kind the payload omits is left ALONE rather than written empty —
 * `undefined` means "this save said nothing about that graph", and an omitted
 * graph must never be turned into an authored empty one.
 *
 * @param {{body: string, face: string, events: string}} keys
 *   from getStorageKeysFor(charId)
 * @param {object|null} blueprintData the payload that was just saved
 * @returns {Array<{kind: string, key: string, value: string}>}
 */
export function blueprintCacheWrites(keys, blueprintData) {
  const out = []
  for (const kind of ['body', 'face', 'events']) {
    const graph = blueprintData?.[kind]
    const key = keys?.[kind]
    if (graph === undefined || graph === null || !key) continue
    out.push({ kind, key, value: JSON.stringify(graph) })
  }
  return out
}

/**
 * Apply those writes. A storage failure (quota, blocked) is reported and
 * skipped rather than thrown: the save itself already succeeded, and failing
 * loudly here would report a completed push as a failed one. The cost of a
 * skipped write is the pre-fix behaviour for that one graph — it re-reads from
 * the server on the next load — so it degrades to correct-but-slower, never to
 * wrong data.
 *
 * @returns {number} how many caches were written
 */
export function writeBlueprintCaches(keys, blueprintData, storage) {
  const store = storage ?? (typeof localStorage !== 'undefined' ? localStorage : null)
  if (!store) return 0
  let written = 0
  for (const { kind, key, value } of blueprintCacheWrites(keys, blueprintData)) {
    try {
      store.setItem(key, value)
      written++
    } catch (e) {
      console.warn(`[blueprintCacheSync] could not refresh the ${kind} cache after save:`, e)
    }
  }
  return written
}
